import { useState, useEffect, useMemo } from "react";
import {
  API_BASE_URL,
  API_PUBLIC_AUTH_TOKEN,
  API_ENDPOINTS,
} from "../constants/api";
import {
  MaintenanceConfigResponse,
  MaintenanceState,
  DEFAULT_MAINTENANCE_STATE,
} from "../types/maintenance-page";
import state from "../maintenance-page/state.json";
import { IS_LOCALHOST } from "../constants/environment";

/** Cache the maintenance state to avoid repeated fetches */
let cachedMaintenanceState: MaintenanceState | null = null;

/** Track if maintenance config has been loaded */
let maintenanceConfigLoaded = false;

/** Export the cached maintenance state for direct access in other modules */
export const getCachedMaintenanceState = () => cachedMaintenanceState;

/** Export whether maintenance config has been loaded */
export const isMaintenanceConfigLoaded = () => maintenanceConfigLoaded;

/** Reset cache and loaded state (for testing only) */
export const resetMaintenanceCache = () => {
  cachedMaintenanceState = null;
  maintenanceConfigLoaded = false;
};

/** Fetch maintenance config */
const fetchMaintenanceConfig = async (): Promise<MaintenanceState> => {
  const url = `${API_BASE_URL}${API_ENDPOINTS.MAINTENANCE_CONFIG}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (API_PUBLIC_AUTH_TOKEN) {
    headers.Authorization = API_PUBLIC_AUTH_TOKEN;
  }

  const response = await fetch(url, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    throw new Error(`Maintenance config fetch failed: ${response.status}`);
  }

  const data: MaintenanceConfigResponse = await response.json();

  if (data && typeof data.enabled === "boolean") {
    return {
      enabled: data.enabled,
      dateTime: data.dateTime,
    };
  }

  throw new Error("Invalid maintenance config response format");
};

export const useMaintenance = () => {
  const [maintenanceState, setMaintenanceState] = useState<MaintenanceState>(
    cachedMaintenanceState || DEFAULT_MAINTENANCE_STATE
  );
  const [isLoading, setIsLoading] = useState(!cachedMaintenanceState);

  useEffect(() => {
    // If we already have cached state, don't fetch again
    if (cachedMaintenanceState) {
      setIsLoading(false);
      maintenanceConfigLoaded = true;
      return;
    }

    const loadConfig = async () => {
      try {
        // On localhost, use the local state.json directly without making HTTP requests
        if (IS_LOCALHOST) {
          cachedMaintenanceState = state;
          setMaintenanceState(cachedMaintenanceState);
          maintenanceConfigLoaded = true;
          return;
        }

        const fetchedState = await fetchMaintenanceConfig();
        cachedMaintenanceState = fetchedState;
        setMaintenanceState(fetchedState);
        maintenanceConfigLoaded = true;
      } catch (error) {
        console.error("Error fetching maintenance config:", error);
        // Fail-closed: default to maintenance enabled when API fails
        // This ensures the site shows maintenance if the config endpoint is down
        const failClosedState: MaintenanceState = {
          enabled: false,
          dateTime: undefined,
        };
        cachedMaintenanceState = failClosedState;
        setMaintenanceState(failClosedState);
        maintenanceConfigLoaded = true;
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, []);

  return useMemo(
    () => ({
      maintenanceState,
      isLoading,
    }),
    [maintenanceState, isLoading]
  );
};
